import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChartLine } from "lucide-react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "@/components/ui/chart";
import { RevenueChartCard } from "../revenue/RevenueChartCard";
import { RevenueChartLegend } from "../revenue/RevenueChartLegend";
import { REVENUE_STACKED_AREA_CHART_HEIGHT } from "../revenue/constants";
import { SERVICES_CHART_CARD_ICON_COLOR } from "../services/constants";
import {
  AnalyticsAreaFillDefs,
  analyticsAreaFillUrl,
  useAnalyticsChartInstanceId,
} from "../shared/chartAreaFill";
import { formatAnalyticsDayLabel } from "../shared/analyticsFormatting";
import { REVIEWS_COLORS } from "./constants";
import type { PreCheckoutTrendPoint } from "./types";

function ReviewsPreCheckoutTooltip({
  active,
  payload,
  label,
  series,
}: {
  active?: boolean;
  payload?: Array<{ dataKey?: string; value?: number; color?: string }>;
  label?: string;
  series: Array<{ key: string; label: string; color: string }>;
}) {
  if (!active || !payload?.length) {
    return null;
  }

  const seriesByKey = Object.fromEntries(
    series.map((item) => [item.key, item]),
  );

  return (
    <div className="border-border/50 bg-background grid min-w-[10rem] gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl">
      <p className="font-medium text-zinc-900">
        {formatAnalyticsDayLabel(label)}
      </p>
      {payload.map((item) => {
        const config = seriesByKey[String(item.dataKey)];
        if (!config) {
          return null;
        }

        return (
          <div
            key={config.key}
            className="flex items-center justify-between gap-4"
          >
            <div className="flex items-center gap-2">
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: config.color }}
              />
              <span className="text-muted-foreground">{config.label}</span>
            </div>
            <span className="font-medium text-zinc-900">
              {Math.round(Number(item.value ?? 0))}
            </span>
          </div>
        );
      })}
    </div>
  );
}

interface ReviewsPreCheckoutChartProps {
  data: PreCheckoutTrendPoint[];
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
}

function buildYAxisTicks(maxValue: number): number[] {
  if (maxValue <= 0) {
    return [0, 1];
  }

  if (maxValue <= 5) {
    return Array.from({ length: maxValue + 1 }, (_, index) => index);
  }

  const step = Math.max(1, Math.ceil(maxValue / 5));
  const ticks: number[] = [0];

  for (let value = step; value < maxValue; value += step) {
    ticks.push(value);
  }

  ticks.push(maxValue);
  return ticks;
}

export const ReviewsPreCheckoutChart: React.FC<
  ReviewsPreCheckoutChartProps
> = ({ data, onExport, exportDisabled }) => {
  const { t } = useTranslation();

  const yAxisMax = useMemo(() => {
    const peak = data.reduce((max, point) => {
      const total = point.preCheckoutReviews + point.postStayReviews;
      return Math.max(max, total);
    }, 0);

    return Math.max(peak, 1);
  }, [data]);

  const yAxisTicks = useMemo(() => buildYAxisTicks(yAxisMax), [yAxisMax]);

  const series = useMemo(
    () => [
      {
        key: "postStayReviews" as const,
        label: t("analytics.reviews.postStayReviews", "Post-stay reviews"),
        color: REVIEWS_COLORS.postStay,
      },
      {
        key: "preCheckoutReviews" as const,
        label: t(
          "analytics.reviews.preCheckoutReviews",
          "Pre-check-out reviews",
        ),
        color: REVIEWS_COLORS.preCheckout,
      },
    ],
    [t],
  );

  const chartConfig = series.reduce<ChartConfig>((config, item) => {
    config[item.key] = {
      label: item.label,
      color: item.color,
    };
    return config;
  }, {});
  const chartInstanceId = useAnalyticsChartInstanceId();

  return (
    <RevenueChartCard
      title={t("analytics.reviews.preCheckoutTitle", "Pre-check-out reviews")}
      icon={<ChartLine />}
      iconColor={SERVICES_CHART_CARD_ICON_COLOR}
      width="full"
      className="min-h-0 flex-1"
      onExport={onExport}
      exportDisabled={exportDisabled}
    >
      <div className="flex flex-col gap-3">
        <ChartContainer
          config={chartConfig}
          className="!aspect-auto w-full"
          style={{ height: REVENUE_STACKED_AREA_CHART_HEIGHT }}
        >
          <AreaChart
            data={data}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          >
            <defs>
              <AnalyticsAreaFillDefs
                seriesKeys={series.map((item) => item.key)}
                chartInstanceId={chartInstanceId}
              />
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="day"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              interval={4}
              className="text-muted-foreground text-xs"
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={40}
              allowDecimals={false}
              domain={[0, yAxisMax]}
              ticks={yAxisTicks}
              className="text-muted-foreground text-xs"
            />
            <ChartTooltip
              cursor={false}
              content={<ReviewsPreCheckoutTooltip series={series} />}
            />
            {series.map((item) => (
              <Area
                key={item.key}
                type="monotone"
                dataKey={item.key}
                stackId="reviews"
                stroke={`var(--color-${item.key})`}
                fill={analyticsAreaFillUrl(item.key, chartInstanceId)}
                strokeWidth={1.5}
              />
            ))}
          </AreaChart>
        </ChartContainer>
        <RevenueChartLegend items={series} />
      </div>
    </RevenueChartCard>
  );
};
